import {
  useCallback,
  useRef,
  PropsWithChildren,
  useEffect,
  useReducer,
  useLayoutEffect,
  useMemo,
} from "react";
import { MdsIconKind } from "@/design-system/components/icon";
import {
  MdsDropdownContentList,
  MdsDropdownItemKind,
  MdsDropdownItem,
} from "@/design-system/components/dropdown";
import { MdsFloatingDropdownContent } from "@/design-system/components/dropdown/MdsFloatingDropdownContent";
import { CommonEditorInput } from "@/mds/components/forms/common-editor-input.tsx/common-editor-input";
import {
  InstructionsEditorMentionItem,
  InstructionsEditorState,
} from "./instructions-editor.types";
import { instructionsEditorReducer } from "./instructions-editor.reducer";
import {
  StyledContainer,
  StyledInputContainer,
  StyledInputArea,
  StyledExpand,
} from "./instructions-editor.style";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import { JsonObject } from "type-fest";
import { InstructionsEditorMention } from "./instructions-editor.types";
import { mdsSpacings } from "@/design-system/foundations";
import { css } from "@/domains/emotion";
import { ZIndex } from "@/domains/design/constants";

export type InstructionsEditorFetchDataHandler<T extends InstructionsEditorMentionItem> = ({
  query,
  mentionSymbol,
}: {
  query: string;
  mentionSymbol?: string;
}) => Promise<InstructionsEditorDataSource<T>>;

export type InstructionsEditorSubmitHandler = (payload: {
  tiptapJsonContent: JsonObject;
  mdxContent: string;
  htmlContent: string;
  mentions: InstructionsEditorMention[];
}) => void;

interface Props<T extends InstructionsEditorMentionItem> extends PropsWithChildren {
  placeholder?: string;
  isDisabled?: boolean;
  loadingDelayMs?: number;
  autoFocus?: boolean;
  fetchData: InstructionsEditorFetchDataHandler<T>;
  onSubmit?: InstructionsEditorSubmitHandler;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChange?: () => void;
  inputRef?: React.RefObject<MemCommonRichTextInputInstance>;
  editorDimensions?: {
    minHeight?: number;
    maxHeight?: number;
    padding?: { top: number; right: number; bottom: number; left: number };
  };
  setContent?: string;
  onContentModified?: () => void;
  className?: string;
}

export type InstructionsEditorDataSource<T extends InstructionsEditorMentionItem> = {
  data: T[];
  error?: string;
  isLoading?: boolean;
};

export const InstructionsEditor = <T extends InstructionsEditorMentionItem>(props: Props<T>) => {
  const {
    fetchData,
    placeholder,
    isDisabled = false,
    loadingDelayMs = 300,
    autoFocus = false,
    onSubmit,
    onInputFocus,
    onInputBlur,
    onInputChange,
    inputRef: externalInputRef,
    editorDimensions,
    setContent,
    onContentModified,
    className,
  } = props;

  const [state, dispatch] = useReducer(instructionsEditorReducer<T>, {
    isOpen: false,
    query: "",
    isEmpty: true,
    isLoading: false,
    data: [],
    hasSearched: false,
    keyboardSelectionIndex: -1,
  } as InstructionsEditorState<T>);

  const rootRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<MemCommonRichTextInputInstance>(null);
  const inputRef = externalInputRef || internalInputRef;

  // Track ongoing requests to handle race conditions
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set the content of the editor
  useLayoutEffect(() => {
    if (!setContent) return;

    // Wait for editor to be initialized.
    const h = setTimeout(() => {
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.SetEditorContent,
        payload: { content: setContent },
      });
    }, 10);

    return () => clearTimeout(h);
  }, [inputRef, setContent]);

  const cleanupLoadingTimeout = () => {
    if (!loadingTimeoutRef.current) {
      return;
    }

    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = null;
  };

  // Handle mention queries
  const handleMentionQuery = useCallback(
    async (queryStr: string, clientRect?: DOMRect) => {
      const trimmedQuery = queryStr.trim();

      // Check if query starts with mention symbols
      const startsWithMentionSymbol = ["@", "#"].includes(trimmedQuery.charAt(0));

      if (!startsWithMentionSymbol) {
        // Close popover and clear data if no mention symbol
        dispatch({ type: "RESET" });

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Cancel any ongoing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        return;
      }

      const mentionSymbol = trimmedQuery.charAt(0);
      const query = trimmedQuery.slice(1); // Remove the @ or # symbol

      // We want to load even if the query is empty
      if (trimmedQuery.length >= 0) {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Set up delayed loading state
        loadingTimeoutRef.current = setTimeout(() => {
          if (!abortController.signal.aborted) {
            dispatch({ type: "LOADING_STARTED" });
          }
        }, loadingDelayMs);

        try {
          const result = await fetchData({ query, mentionSymbol });

          // Only update if this request wasn't cancelled
          if (!abortController.signal.aborted) {
            dispatch({ type: "SEARCH_COMPLETED", payload: result.data });
            cleanupLoadingTimeout();
          }
        } catch (error) {
          if (abortController.signal.aborted) {
            return;
          }

          console.error("Failed to fetch data:", error);
          dispatch({ type: "SEARCH_FAILED" });
          cleanupLoadingTimeout();
        } finally {
          dispatch({ type: "LOADING_STOPPED" });
        }
      }

      dispatch({ type: "QUERY_CHANGED", payload: query });
      dispatch({ type: "CLIENT_RECT_CHANGED", payload: clientRect });
    },

    [fetchData, loadingDelayMs]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Handle editor empty state changes
  const handleIsEmptyChange = useCallback((empty: boolean) => {
    dispatch({ type: "EMPTY_STATE_CHANGED", payload: empty });
  }, []);

  const handleSelection = useCallback(
    async (id: string) => {
      const item = state.data.find(item => item.id === id);

      if (!item) return;

      const label = (await item.onClick?.()) ?? item.label;

      const kind = (() => {
        if (item.kind === "create-new-collection") {
          return MemCommonMentionKind.Collection;
        }
        return item.kind ?? MemCommonMentionKind.Note;
      })();

      inputRef.current?.dispatchAction?.({
        kind: MemCommonRichTextInputActionKind.CloseMention,
        payload: {
          id: item?.id,
          kind,
          label,
          // action,
        },
      });

      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });

      dispatch({ type: "POPOVER_CLOSED" });
      dispatch({ type: "QUERY_CHANGED", payload: "" });
    },
    [state.data, inputRef]
  );

  const handleArrowKeyPressed = useCallback(
    (direction: "up" | "down") => {
      if (!state.isOpen || state.data.length === 0) return;

      // Handle keyboard navigation for inline mentions dropdown
      const keyboardSelectionIndex =
        direction === "up"
          ? state.keyboardSelectionIndex <= 0
            ? state.data.length - 1
            : state.keyboardSelectionIndex - 1
          : state.keyboardSelectionIndex >= state.data.length - 1
            ? 0
            : state.keyboardSelectionIndex + 1;

      console.log("keyboardSelectionIndex", keyboardSelectionIndex);
      dispatch({
        type: "KEYBOARD_SELECTION_CHANGED",
        payload: keyboardSelectionIndex,
      });
    },
    [state.isOpen, state.data, state.keyboardSelectionIndex]
  );

  const handleEnterPressed = useCallback(() => {
    // If inline mentions dropdown is open and we have keyboard selection, select that item
    if (state.isOpen && state.data.length > 0 && state.keyboardSelectionIndex >= 0) {
      const selectedItem = state.data[state.keyboardSelectionIndex];
      if (selectedItem) {
        handleSelection(selectedItem.id);
        return;
      }
    }
  }, [state.isOpen, state.data, state.keyboardSelectionIndex, handleSelection]);

  const isQueryEmpty = !state.query.length;

  const handleSubmit = useCallback(
    (payload: {
      mdxContent: string;
      htmlContent: string;
      markdownContent: string;
      tiptapJsonContent: JsonObject;
    }) => {
      const { mdxContent, htmlContent, tiptapJsonContent } = payload;

      // Parse mentions from the markdownContent
      const mentions = parseMentionsFromMarkdown(mdxContent);

      onSubmit?.({
        htmlContent,
        mdxContent,
        tiptapJsonContent,
        mentions,
      });
    },
    [onSubmit]
  );

  // Convert instructions editor data to dropdown format for inline mentions
  const dropdownContentList: MdsDropdownContentList = useMemo(() => {
    if (!state.data.length) return { items: [] };

    const items: MdsDropdownItem[] = state.data.map((item, index) => ({
      id: item.id,
      kind: MdsDropdownItemKind.Button,
      label: item.label,
      subtitle: undefined,
      iconKind:
        item.kind === "create-new-collection"
          ? MdsIconKind.Plus
          : item.kind === MemCommonMentionKind.Collection
            ? MdsIconKind.Collection
            : MdsIconKind.Document,
      scrollIntoView: index === state.keyboardSelectionIndex,
      isSelected: index === state.keyboardSelectionIndex,
      onClick: async ({ itemId }) => {
        const selectedItem = state.data.find(dataItem => dataItem.id === itemId);
        if (selectedItem) {
          const label = (await selectedItem.onClick?.()) ?? selectedItem.label;

          inputRef.current?.dispatchAction?.({
            kind: MemCommonRichTextInputActionKind.CloseMention,
            payload: {
              id: selectedItem.id,
              kind:
                selectedItem.kind === "create-new-collection"
                  ? MemCommonMentionKind.Collection
                  : selectedItem.kind,
              label,
            },
          });
        }

        // Clear mention state
        dispatch({ type: "CLIENT_RECT_CHANGED", payload: undefined });
        dispatch({ type: "QUERY_CHANGED", payload: "" });
        dispatch({ type: "RESET" });
      },
    }));

    switch (state.data[0].kind) {
      case MemCommonMentionKind.Collection:
        items.unshift({
          id: "header",
          kind: MdsDropdownItemKind.Detail,
          className: detailPaddingStyles,
          text: `Tag a${isQueryEmpty ? " recent" : ""} collection`,
        });
        break;
      case MemCommonMentionKind.Note:
        items.unshift({
          id: "header",
          kind: MdsDropdownItemKind.Detail,
          className: detailPaddingStyles,
          text: `Mention a${isQueryEmpty ? " recent" : ""} note`,
        });
        break;
    }

    return { items };
  }, [isQueryEmpty, state.data, inputRef, state.keyboardSelectionIndex]);

  // Handle dropdown hover for inline mentions
  const handleDropdownHover = useCallback(
    ({ itemId }: { itemId?: string }) => {
      if (!itemId) return;

      const index = state.data.findIndex(item => item.id === itemId);
      if (index >= 0) {
        dispatch({ type: "KEYBOARD_SELECTION_CHANGED", payload: index });
      }
    },
    [state.data]
  );

  // Handle dropdown open change for inline mentions
  const handleDropdownOpenChange = useCallback((open: boolean, _event?: Event, reason?: string) => {
    if (!open && reason && (reason === "escape-key" || reason === "outside-press")) {
      dispatch({ type: "CLIENT_RECT_CHANGED", payload: undefined });
      dispatch({ type: "QUERY_CHANGED", payload: "" });
      dispatch({ type: "RESET" });
    }

    // Reset keyboard selection when dropdown closes (only for inline mentions)
    if (!open) {
      dispatch({ type: "KEYBOARD_SELECTION_CHANGED", payload: -1 });
    }
  }, []);

  return (
    <StyledContainer
      ref={rootRef}
      data-mds-name="instructions-editor"
      translate="no"
      className={className}
    >
      <MdsFloatingDropdownContent
        zIndex={ZIndex.Dropdown}
        clientRect={state.clientRect}
        placement="bottom-start"
        contentList={dropdownContentList}
        onHover={handleDropdownHover}
        onOpenChange={handleDropdownOpenChange}
        offsetOptions={[{ mainAxis: 10, crossAxis: 0 }]}
        rootBoundary="document"
      />

      <StyledInputContainer
        aria-disabled={isDisabled}
        data-mds-name="instructions-editor-input-container"
      >
        <StyledInputArea>
          <StyledExpand>
            <CommonEditorInput
              dimensions={editorDimensions}
              onMentionQuerySet={handleMentionQuery}
              onIsEmptyChange={handleIsEmptyChange}
              placeholder={placeholder}
              isDisabled={isDisabled}
              inputRef={inputRef}
              onSubmit={handleSubmit}
              onArrowKeyPressed={handleArrowKeyPressed}
              onEnterPressed={handleEnterPressed}
              onInputFocus={onInputFocus}
              onInputBlur={onInputBlur}
              onInputChange={onInputChange}
              onContentModified={onContentModified}
              content={setContent}
              autoFocus={autoFocus}
            />
          </StyledExpand>
        </StyledInputArea>
      </StyledInputContainer>
    </StyledContainer>
  );
};

// Function to parse mentions from markdownContent
const parseMentionsFromMarkdown = (markdownContent: string): InstructionsEditorMention[] => {
  const mentions: InstructionsEditorMention[] = [];
  const mentionRegex = /<a[^>]*data-type="mention-(note|collection)"[^>]*>/g;

  let match;
  while ((match = mentionRegex.exec(markdownContent)) !== null) {
    const anchorTag = match[0];

    // Extract data attributes
    const idMatch = anchorTag.match(/data-mention-id="([^"]+)"/);
    const kindMatch = anchorTag.match(/data-mention-kind="([^"]+)"/);
    const labelMatch = anchorTag.match(/data-mention-fallback-label="([^"]+)"/);

    if (idMatch && kindMatch && labelMatch) {
      const id = idMatch[1];
      const kind = kindMatch[1];
      const label = labelMatch[1];

      // Convert string kind to MemCommonMentionKind enum value
      if (kind === "collection") {
        mentions.push({
          kind: MemCommonMentionKind.Collection,
          collection_id: id,
          collection_title: label,
        });
      } else if (kind === "note") {
        mentions.push({
          kind: MemCommonMentionKind.Note,
          note_id: id,
          note_primary_label: label,
        });
      }
    }
  }

  return mentions;
};

const detailPaddingStyles = css({
  padding: `${mdsSpacings().sm} 0 ${mdsSpacings().xs} ${mdsSpacings().smPlus}`,
});
